// src/components/FormPreview.jsx
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Alert,
  IconButton,
  LinearProgress,
  Grid,
  InputLabel,
  FormLabel,
  Stack,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTitleProps,
  StackProps,
  StackDirection,
  StackItemProps,
  StackItem,
  Slider,
  Rating,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { notification, Result, Button as AntButton } from 'antd';
import {
  CloudUploadOutlined,
  CloseOutlined,
  FileOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlusOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useState, useCallback, useEffect, useRef } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import '../../styles/FormPreview.css';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useNavigate, useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { useSelector } from "react-redux";
import CalculationField from './fields/CalculationField';
import { FileUploadField } from './fields/FileUploadField';
import { ImageUploadField } from './fields/ImageUploadField';
import { ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon, Download as DownloadIcon, Visibility as VisibilityIcon } from '@mui/icons-material';

// FilePreview bileşenini ekleyin
const FilePreview = ({ file, onRemove, showProgress }) => {
  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return <FileImageOutlined />;
    if (fileType.includes('pdf')) return <FilePdfOutlined />;
    return <FileTextOutlined />;
  };

  return (
    <Paper
      sx={{
        mt: 1,
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#f5f5f5'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getFileIcon(file.type)}
        <Typography 
          variant="body2" 
          sx={{ 
            maxWidth: 'none !important',
            whiteSpace: 'pre-wrap !important',
            wordBreak: 'break-word !important',
            overflow: 'visible !important',
            textOverflow: 'initial !important'
          }}
        >
          {file.name}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showProgress && (
          <Box sx={{ width: 100 }}>
            <LinearProgress variant="determinate" value={100} />
          </Box>
        )}
        <IconButton size="small" onClick={onRemove} color="error">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

// Sabit değişkenleri component dışına taşıyalım
const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB sabit boyut
const MAX_FILES = 50; // Maksimum dosya sayısı
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff'];
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/zip',
  'application/x-rar-compressed',
  'application/x-7z-compressed'
];
const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv', '.zip', '.rar', '.7z'];

const FormPreview = ({ 
  id, 
  formTitle, 
  formDescription, 
  formFields = [], 
  settings,
  showPdfButton = true,
  formLibrary,
  logoUrl,
  showSubmitButton = true, // Varsayılan olarak gönder butonu görünür
  calculationValues = {},
  onSubmit,
  submitting = false // Form gönderilirken loading durumu
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [files, setFiles] = useState({});
  const [uploading, setUploading] = useState({});
  const [signatureData, setSignatureData] = useState({});
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const signatureRefs = useRef({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const [tableRowCounts, setTableRowCounts] = useState({});
  const { formId } = useParams(); // URL'den formId'yi al
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);
  const theme = useTheme();
  const matchesXS = useMediaQuery(theme.breakpoints.down('sm'));
  const matchesSM = useMediaQuery(theme.breakpoints.down('md'));
  const [currentPage, setCurrentPage] = useState(1);
  const maxPage = Math.max(...(formFields || []).map(f => f.page || 1), 1);
  const currentFields = (formFields || []).filter(f => (f.page || 1) === currentPage);
  // Form içeriğini parse et
 /*  const formContent = JSON.parse(formLibrary.formContent); */

  const validateField = useCallback((field, value) => {
    if (!field) {
      return ''; // Tablo hücreleri için hata mesajı gösterme
    }
    if (field.required && (!value || value.length === 0)) {
      return 'Bu alan zorunludur';
    }
    if (field.type === 'file') {
      if (field.required && (!value || value.length === 0)) {
        return 'Dosya seçmelisiniz';
      }
      if (value && field.maxSize && value.size > field.maxSize) {
        return `Dosya boyutu ${(field.maxSize / 1024 / 1024).toFixed(1)}MB'dan küçük olmalıdır`;
      }
    }
    return '';
  }, [formData]);

  const handleChange = useCallback((fieldId, value) => {
    setFormData(prev => {
      const newFormData = { ...prev };
      
      // fieldId'yi string'e çevir
      const fieldIdStr = String(fieldId);
      
      if (fieldIdStr.includes('_firstName') || fieldIdStr.includes('_lastName')) {
        // Ad veya soyad alanları için direkt güncelleme
        newFormData[fieldIdStr] = value;
      } else if (fieldIdStr.includes('-')) {
        // Tablo alanları için mevcut işlem
        const [tableId, rowIndex, colIndex] = fieldIdStr.split('-');
        if (!newFormData[tableId]) {
          newFormData[tableId] = { rows: [] };
        }
        while (newFormData[tableId].rows.length <= parseInt(rowIndex)) {
          newFormData[tableId].rows.push({});
        }
        newFormData[tableId].rows[rowIndex][colIndex] = value;
      } else {
        // Diğer tüm alanlar için
        newFormData[fieldIdStr] = value;
      }
      
      return newFormData;
    });
  
    // Hata kontrolü
    const field = formFields.find(f => f.id.toString() === fieldId.toString());
    if (field) {
      const error = validateField(field, value);
      setErrors(prev => ({
        ...prev,
        [fieldId]: error
      }));
    }
  }, [formFields, validateField]);

  // İmza için state güncelleme fonksiyonu
  const handleSignatureEnd = (fieldId) => {
    if (signatureRefs.current[fieldId]) {
        const data = signatureRefs.current[fieldId].getTrimmedCanvas().toDataURL('image/png');
        setSignatureData(prev => ({
            ...prev,
            [fieldId]: data
        }));
        setFormData(prev => ({
            ...prev,
            [fieldId]: data
        }));
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      const formDataFields = {};
      formFields.forEach(field => {
        if (field.type === 'table') {
          formDataFields[field.id] = {
            value: {
              rows: formData[field.id]?.rows || []
            },
            label: field.label,
            type: field.type
          };
        } else if (field.type === 'file' || field.type === 'image' || field.type === 'pdf') {
          const fieldFiles = files[field.id] || [];
          formDataFields[field.id] = {
            value: fieldFiles.map(file => ({
              name: file.name,
              type: file.type,
              size: file.size,
              base64: file.base64
            })),
            label: field.label,
            type: field.type
          };
        } else if (field.type === 'pdfDocument') {
          // PDF Doküman Eki - form oluşturan kişinin eklediği referans PDF
          formDataFields[field.id] = {
            value: field.uploadedFiles || [],
            label: field.label,
            type: field.type,
            isReference: true
          };
        } else if (field.type === 'signature') {
          formDataFields[field.id] = {
            value: signatureData[field.id] || "",
            label: field.label,
            type: field.type
          };
        } else {
          formDataFields[field.id] = {
            value: field.type === 'checkbox' ? 
              (Array.isArray(formData[field.id]) ? formData[field.id] : []) : 
              (formData[field.id] || ''),
            label: field.label,
            type: field.type
          };
        }
      });

      const submissionData = {
        title: formTitle,
        description: formDescription,
        fields: formFields,
        formData: formDataFields,
        settings: settings
      };

      console.log('Gönderilecek form verisi:', submissionData);

      if (onSubmit) {
        const result = await onSubmit(submissionData);
        if (result) {
          setSubmitted(true);
          setConfirmModalOpen(false);
          
          notification.success({
            message: 'Başarılı!',
            description: 'Form başarıyla gönderildi.',
            placement: 'topRight',
            duration: 4,
            style: {
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid #b7eb8f'
            },
            icon: <CheckCircleOutlined style={{ 
              fontSize: '24px', 
              color: '#52c41a',
              marginRight: '8px',
              marginTop: '2px'
            }} />
          });
        }
      }
    } catch (error) {
      console.error('Form gönderimi hatası:', error);
      setConfirmModalOpen(false);
      
      notification.error({
        message: 'Hata!',
        description: error.message || 'Form gönderimi sırasında bir hata oluştu.',
        placement: 'topRight',
        duration: 4,
        style: {
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: '1px solid #ffa39e'
        },
        icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
      });
    }
  };

  const handleFileChange = useCallback(async (fieldId, fileList, isImageField = false, isPdfField = false) => {
    try {
      const selectedFiles = Array.from(fileList);
      const currentFiles = files[fieldId] || [];
      if (currentFiles.length + selectedFiles.length > MAX_FILES) {
        setErrors(prev => ({
          ...prev,
          [fieldId]: `En fazla ${MAX_FILES} ${isPdfField ? 'PDF' : isImageField ? 'görsel' : 'dosya'} yükleyebilirsiniz`
        }));
        return;
      }

      // Dinamik olarak field.accept'tan uzantı ve mime type listesi oluştur
      const field = formFields.find(f => f.id === fieldId);
      let allowedExtensions = ALLOWED_FILE_EXTENSIONS.map(ext => ext.toLowerCase());
      let allowedMimeTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
        'text/csv',
        'application/zip',
        'application/x-rar-compressed',
        'application/x-7z-compressed'
      ];
      
      // PDF field için özel filtreleme
      if (isPdfField) {
        allowedExtensions = ['.pdf'];
        allowedMimeTypes = ['application/pdf'];
      } else if (field && field.accept) {
        allowedExtensions = field.accept.split(',')
          .filter(x => x.startsWith('.'))
          .map(x => x.trim().toLowerCase());
        allowedMimeTypes = field.accept.split(',')
          .filter(x => !x.startsWith('.'))
          .map(x => x.trim());
      }

      const processedFiles = await Promise.all(
        selectedFiles.map(async (file) => {
          const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
          const isImage = file.type.startsWith('image/');
          const isPdf = file.type === 'application/pdf' || fileExt === '.pdf';
          
          if (isImageField && !isImage) {
            throw new Error('Sadece görsel dosyaları yükleyebilirsiniz');
          }
          
          if (isPdfField && !isPdf) {
            throw new Error('Sadece PDF dosyaları yükleyebilirsiniz');
          }
          
          if (!isImageField && !isPdfField) {
            // Eğer accept hiç yoksa veya hem uzantı hem mime type listesi boşsa, dosyayı kabul et
            if (
              (allowedExtensions.length > 0 || allowedMimeTypes.length > 0) &&
              ((allowedExtensions.length > 0 && !allowedExtensions.includes(fileExt)) &&
               (allowedMimeTypes.length > 0 && !allowedMimeTypes.includes(file.type)))
            ) {
              throw new Error(`Geçersiz dosya formatı: ${file.name}. Desteklenen formatlar: ${allowedExtensions.join(', ')}`);
            }
          }
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                name: file.name,
                type: file.type,
                size: file.size,
                base64: reader.result
              });
            };
            reader.onerror = () => {
              reject(new Error('Dosya okuma hatası'));
            };
            reader.readAsDataURL(file);
          });
        })
      );

      setFiles(prev => ({
        ...prev,
        [fieldId]: [...(prev[fieldId] || []), ...processedFiles]
      }));

      setFormData(prev => ({
        ...prev,
        [fieldId]: [...(prev[fieldId] || []), ...processedFiles]
      }));

      setErrors(prev => ({
        ...prev,
        [fieldId]: ''
      }));

    } catch (error) {
      setErrors(prev => ({
        ...prev,
        [fieldId]: error.message
      }));
    } finally {
      setUploading(prev => ({ ...prev, [fieldId]: false }));
    }
  }, [files, formFields]);

  const handleRemoveFile = useCallback((fieldId, fileIndex) => {
    setFiles(prev => {
      const newFiles = { ...prev };
      if (newFiles[fieldId]) {
        newFiles[fieldId] = newFiles[fieldId].filter((_, index) => index !== fileIndex);
      }
      return newFiles;
    });

    setFormData(prev => {
      const newFormData = { ...prev };
      if (newFormData[fieldId]) {
        newFormData[fieldId] = newFormData[fieldId].filter((_, index) => index !== fileIndex);
      }
      return newFormData;
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    formFields.forEach(field => {
      if (field.type === 'name') {
        const firstName = formData[`${field.id}_firstName`];
        const lastName = formData[`${field.id}_lastName`];
        if (field.required && (!firstName || !lastName)) {
          newErrors[`${field.id}_firstName`] = !firstName ? 'Ad alanı zorunludur' : '';
          newErrors[`${field.id}_lastName`] = !lastName ? 'Soyad alanı zorunludur' : '';
        }
      } else {
        const value = formData[field.id];
        const error = validateField(field, value);
        if (error) {
          newErrors[field.id] = error;
        }
      }
    });
  
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setConfirmModalOpen(true);
    }
  };

  const renderFormPreview = () => {
    return (
      <Box 
        sx={{ 
          p: 3, 
          maxHeight: '70vh', 
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f5f9',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e1',
            borderRadius: '4px',
            '&:hover': {
              background: '#94a3b8'
            }
          }
        }}
      >
        {/* Form Başlık */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
            {formTitle}
          </Typography>
          
          {formDescription && (
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {formDescription}
            </Typography>
          )}
        </Box>

        {/* Form Alanları */}
        <Grid container spacing={3}>
          {currentFields.map((field) => (
            <Grid item xs={12} key={field.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'grey.50'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Alan Etiketi */}
                  <Box sx={{ minWidth: '200px' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 0.5
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block'
                      }}
                    >
                      {field.required ? 'Zorunlu Alan' : 'Opsiyonel Alan'}
                    </Typography>
                  </Box>

                  {/* Alan Değeri */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        minHeight: '40px',
                        wordBreak: 'break-word'
                      }}
                    >
                      {(() => {
                        const value = formData[field.id];
                        switch (field.type) {
                          case 'calculation':
                            if (!value?.rows?.length) return 'Hesaplama verisi yok';
                            return (
                              <CalculationField
                                field={field}
                                value={calculationValues[field.id]}
                                onChange={(value) => handleChange(field.id, value)}
                                formData={formData}
                              />
                            );
                          case 'checkbox':
                            return Array.isArray(value) ? (
                              <Stack direction="column" spacing={1}>
                                {value.map((item, index) => (
                                  <Chip
                                    key={index}
                                    label={item}
                                    size="small"
                                    sx={{ maxWidth: 'fit-content' }}
                                  />
                                ))}
                              </Stack>
                            ) : '';
                          
                          case 'file':
                          case 'image':
                          case 'pdf':
                            console.log('FormPreview render', field.type);
                            const fieldFiles = files[field.id] || [];
                            const isImage = field.type === 'image';
                            const isPdf = field.type === 'pdf';
                            return (
                              <Paper
                                elevation={4}
                                sx={{
                                  p: 3,
                                  border: '2px solid',
                                  borderColor: isPdf ? 'error.main' : isImage ? 'secondary.main' : 'primary.main',
                                  borderRadius: 3,
                                  bgcolor: isPdf ? 'error.lighter' : isImage ? 'secondary.lighter' : 'grey.100',
                                  boxShadow: 6,
                                  mb: 2,
                                  transition: 'all 0.3s',
                                  '&:hover': {
                                    bgcolor: isPdf ? 'error.light' : isImage ? 'secondary.light' : 'primary.lighter',
                                    borderColor: isPdf ? 'error.dark' : isImage ? 'secondary.dark' : 'primary.dark',
                                    boxShadow: 10,
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                                  {isPdf ? (
                                    <FilePdfOutlined sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
                                  ) : isImage ? (
                                    <FileImageOutlined sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
                                  ) : (
                                    <CloudUploadOutlined sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                                  )}
                                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {field.label}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                    {isPdf
                                      ? 'Kullanıcılar buradan PDF dosyası yükleyebilir. Sürükleyip bırakabilir veya tıklayarak seçebilir.'
                                      : isImage
                                      ? 'Kullanıcılar buradan görsel yükleyebilir. Sürükleyip bırakabilir veya tıklayarak seçebilir.'
                                      : 'Kullanıcılar buradan dosya yükleyebilir. Sürükleyip bırakabilir veya tıklayarak seçebilir.'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                    {isPdf
                                      ? 'Desteklenen format: PDF. Maksimum dosya boyutu: 20MB.'
                                      : isImage
                                      ? 'Desteklenen formatlar: JPG, PNG, GIF, WEBP, BMP, TIFF. Maksimum görsel sayısı: 5.'
                                      : 'Desteklenen formatlar: PDF, Word, Excel, PowerPoint, TXT, CSV, ZIP, RAR, 7Z. Maksimum dosya sayısı: 5.'}
                                  </Typography>
                                  {isPdf ? (
                                    <Box sx={{ width: 60, height: 60, bgcolor: 'white', border: '1px dashed #bbb', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                      <FilePdfOutlined style={{ fontSize: 32, color: '#d32f2f' }} />
                                    </Box>
                                  ) : isImage ? (
                                    <Box sx={{ width: 60, height: 60, bgcolor: 'white', border: '1px dashed #bbb', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                      <img src="https://cdn-icons-png.flaticon.com/512/1375/1375106.png" alt="örnek görsel" style={{ width: 40, height: 40, opacity: 0.5 }} />
                                    </Box>
                                  ) : (
                                    <Box sx={{ width: 60, height: 60, bgcolor: 'white', border: '1px dashed #bbb', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                      <FileOutlined style={{ fontSize: 32, color: '#bbb' }} />
                                    </Box>
                                  )}
                                </Box>
                                <FormControl error={!!errors[field.id]} required={field.required} fullWidth className="form-preview-file-field">
                                  <Box className="form-preview-file-upload">
                                    <Button
                                      variant="outlined"
                                      component="label"
                                      size="medium"
                                      startIcon={isPdf ? <FilePdfOutlined /> : isImage ? <FileImageOutlined /> : <CloudUploadOutlined />}
                                      disabled={uploading[field.id]}
                                      sx={{
                                        width: '100%',
                                        height: '120px',
                                        border: '2px dashed',
                                        borderColor: isPdf ? 'error.main' : 'primary.main',
                                        bgcolor: 'background.paper',
                                        '&:hover': {
                                          bgcolor: 'action.hover',
                                          borderColor: isPdf ? 'error.dark' : 'primary.dark'
                                        }
                                      }}
                                    >
                                      <Stack spacing={1} alignItems="center">
                                        {isPdf ? (
                                          <>
                                            <FilePdfOutlined sx={{ fontSize: 40, color: 'error.main' }} />
                                            <Typography variant="body2" color="error">
                                              {uploading[field.id] ? 'Yükleniyor...' : 'PDF Yüklemek İçin Tıklayın'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              veya sürükleyip bırakın
                                            </Typography>
                                          </>
                                        ) : isImage ? (
                                          <>
                                            <FileImageOutlined sx={{ fontSize: 40, color: 'primary.main' }} />
                                            <Typography variant="body2" color="primary">
                                              {uploading[field.id] ? 'Yükleniyor...' : 'Görsel Yüklemek İçin Tıklayın'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              veya sürükleyip bırakın
                                            </Typography>
                                          </>
                                        ) : (
                                          <>
                                            <CloudUploadOutlined sx={{ fontSize: 40, color: 'primary.main' }} />
                                            <Typography variant="body2" color="primary">
                                              {uploading[field.id] ? 'Yükleniyor...' : 'Dosya Yüklemek İçin Tıklayın'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              veya sürükleyip bırakın
                                            </Typography>
                                          </>
                                        )}
                                      </Stack>
                                      <input
                                        type="file"
                                        hidden
                                        multiple
                                        accept={isPdf
                                          ? '.pdf,application/pdf'
                                          : isImage 
                                          ? 'image/jpeg,image/png,image/gif,image/webp,image/bmp,image/tiff'
                                          : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z'}
                                        onChange={(e) => handleFileChange(field.id, e.target.files, isImage, isPdf)}
                                      />
                                    </Button>

                                    {fieldFiles.length > 0 && (
                                      <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                          Yüklenen {isImage ? 'Görseller' : 'Dosyalar'} ({fieldFiles.length}/{MAX_FILES}):
                                        </Typography>
                                        {isImage ? (
                                          <Grid container spacing={2}>
                                            {fieldFiles.map((file, index) => (
                                              <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Paper
                                                  sx={{
                                                    p: 1,
                                                    position: 'relative',
                                                    bgcolor: 'background.paper',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1
                                                  }}
                                                >
                                                  <Box sx={{ position: 'relative', paddingTop: '75%' }}>
                                                    <img
                                                      src={file.base64}
                                                      alt={file.name}
                                                      style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        borderRadius: '4px'
                                                      }}
                                                    />
                                                  </Box>
                                                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '80%' }}>
                                                      {file.name}
                                                    </Typography>
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => handleRemoveFile(field.id, index)}
                                                      disabled={uploading[field.id]}
                                                      sx={{
                                                        color: 'error.main',
                                                        '&:hover': {
                                                          bgcolor: 'error.lighter'
                                                        }
                                                      }}
                                                    >
                                                      <CloseOutlined fontSize="small" />
                                                    </IconButton>
                                                  </Box>
                                                </Paper>
                                              </Grid>
                                            ))}
                                          </Grid>
                                        ) : (
                                          <Stack spacing={1}>
                                            {fieldFiles.map((file, index) => (
                                              <Paper
                                                key={index}
                                                sx={{
                                                  p: 1,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 1,
                                                  bgcolor: 'background.paper',
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  borderRadius: 1
                                                }}
                                              >
                                                <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  <FileOutlined style={{ fontSize: 24, color: '#666' }} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                  <Typography variant="body2" noWrap>
                                                    {file.name}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary">
                                                    {(file.size / 1024).toFixed(1)} KB
                                                  </Typography>
                                                </Box>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleRemoveFile(field.id, index)}
                                                  disabled={uploading[field.id]}
                                                  sx={{
                                                    color: 'error.main',
                                                    '&:hover': {
                                                      bgcolor: 'error.lighter'
                                                    }
                                                  }}
                                                >
                                                  <CloseOutlined fontSize="small" />
                                                </IconButton>
                                              </Paper>
                                            ))}
                                          </Stack>
                                        )}
                                      </Box>
                                    )}

                                    {errors[field.id] && (
                                      <FormHelperText error sx={{ mt: 1 }}>
                                        {errors[field.id]}
                                      </FormHelperText>
                                    )}
                                  </Box>
                                </FormControl>
                              </Paper>
                            );
                          
                          case 'signature':
                            return (
                              <Box className="form-preview-signature-field">
                                <Typography variant="subtitle1" className="form-preview-field-label">
                                    İmza
                                </Typography>
                                <ReactSignatureCanvas
                                    ref={ref => signatureRefs.current[field.id] = ref}
                                    canvasProps={{
                                        className: 'form-preview-signature-canvas'
                                    }}
                                    onEnd={() => handleSignatureEnd(field.id)}
                                />
                                <Box className="form-preview-signature-buttons">
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            if (signatureRefs.current[field.id]) {
                                                const data = signatureRefs.current[field.id].getTrimmedCanvas().toDataURL('image/png');
                                                setSignatureData(prev => ({
                                                    ...prev,
                                                    [field.id]: data
                                                }));
                                                setFormData(prev => ({
                                                    ...prev,
                                                    [field.id]: data
                                                }));
                                            }
                                        }}
                                    >
                                        İmza Kaydet
                                    </Button>
                                    {signatureData[field.id] && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={() => {
                                                if (signatureRefs.current[field.id]) {
                                                    signatureRefs.current[field.id].clear();
                                                    setSignatureData(prev => {
                                                        const newData = { ...prev };
                                                        delete newData[field.id];
                                                        return newData;
                                                    });
                                                    setFormData(prev => {
                                                        const newData = { ...prev };
                                                        delete newData[field.id];
                                                        return newData;
                                                    });
                                                }
                                            }}
                                        >
                                            İmza Sil
                                        </Button>
                                    )}
                                </Box>
                                {signatureData[field.id] && (
                                    <Box sx={{ mt: 2 }}>
                                        <img
                                            src={signatureData[field.id]}
                                            alt="İmza"
                                            style={{
                                                maxWidth: '200px',
                                                maxHeight: '100px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                            );
                          
                          case 'table':
                            if (!value?.rows?.length) return 'Tablo verisi yok';
                            return (
                              <Table size="small" sx={{ mt: 1 }}>
                                <TableBody>
                                  {value.rows.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                      {Object.values(row).map((cell, cellIndex) => (
                                        <TableCell key={cellIndex}>
                                          {cell || '-'}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            );
                          
                          case 'name':
                            const firstName = formData[`${field.id}_firstName`] || '';
                            const lastName = formData[`${field.id}_lastName`] || '';
                            return `${firstName} ${lastName}`.trim() || 'İsim girilmedi';
                          
                          case 'date':
                            return value ? new Date(value).toLocaleDateString('tr-TR') : 'Tarih seçilmedi';
                          
                          case 'time':
                            return value || 'Saat seçilmedi';
                          
                          case 'select':
                          case 'radio':
                            return value || 'Seçim yapılmadı';
                          
                          case 'address':
                            return (
                              <Box
                                sx={{
                                  border: '2px solid #0097a7',
                                  borderRadius: 3,
                                  p: 3,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  bgcolor: '#e0f7fa',
                                  color: '#0097a7',
                                  minHeight: 120,
                                  mb: 1,
                                  boxShadow: 2
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <span style={{ fontSize: 32, color: '#0097a7', marginRight: 8 }}>📍</span>
                                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    Adres Bilgisi
                                  </Typography>
                                </Box>
                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="İl"
                                    value={formData[`${field.id}_il`] || ''}
                                    onChange={e => handleChange(`${field.id}_il`, e.target.value)}
                                    variant="outlined"
                                  />
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="İlçe"
                                    value={formData[`${field.id}_ilce`] || ''}
                                    onChange={e => handleChange(`${field.id}_ilce`, e.target.value)}
                                    variant="outlined"
                                  />
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Mahalle"
                                    value={formData[`${field.id}_mahalle`] || ''}
                                    onChange={e => handleChange(`${field.id}_mahalle`, e.target.value)}
                                    variant="outlined"
                                  />
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Sokak"
                                    value={formData[`${field.id}_sokak`] || ''}
                                    onChange={e => handleChange(`${field.id}_sokak`, e.target.value)}
                                    variant="outlined"
                                  />
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Bina No"
                                      value={formData[`${field.id}_binaNo`] || ''}
                                      onChange={e => handleChange(`${field.id}_binaNo`, e.target.value)}
                                      variant="outlined"
                                    />
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Daire No"
                                      value={formData[`${field.id}_daireNo`] || ''}
                                      onChange={e => handleChange(`${field.id}_daireNo`, e.target.value)}
                                      variant="outlined"
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            );
                          
                          default:
                            return typeof value === 'object' ? 
                              JSON.stringify(value) : 
                              value || 'Veri girilmedi';
                        }
                      })()}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Tablo için varsayılan sütunları güncelle
  const defaultColumns = [
    { 
      header: 'Metin', 
      type: 'text',
      placeholder: 'Metin giriniz',
      width: '30%'
    },
    { 
      header: 'Sayı', 
      type: 'number',
      placeholder: 'Sayı giriniz',
      width: '20%'
    },
    { 
      header: 'Tarih', 
      type: 'date',
      width: '25%'
    },
    { 
      header: 'Seçim', 
      type: 'select', 
      options: ['Seçenek 1', 'Seçenek 2', 'Seçenek 3'],
      width: '25%'
    }
  ];

  const handleAddTableRow = useCallback((fieldId) => {
    setFormData(prev => {
      const currentData = prev[fieldId]?.rows || [];
      return {
        ...prev,
        [fieldId]: {
          rows: [...currentData, {}] // Mevcut verileri koru ve yeni boş satır ekle
        }
      };
    });
    
    setTableRowCounts(prev => ({
      ...prev,
      [fieldId]: (prev[fieldId] || 1) + 1
    }));
  }, []);

  const handleRemoveTableRow = useCallback((fieldId, rowIndex) => {
    setFormData(prev => {
      const currentData = prev[fieldId]?.rows || [];
      return {
        ...prev,
        [fieldId]: {
          rows: currentData.filter((_, idx) => idx !== rowIndex) // Belirli satırı sil
        }
      };
    });
    
    setTableRowCounts(prev => ({
      ...prev,
      [fieldId]: Math.max(1, (prev[fieldId] || 1) - 1)
    }));
  }, []);

  const handleClearTable = useCallback((fieldId) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: {
        rows: [{}] // En az bir boş satır bırak
      }
    }));
    
    setTableRowCounts(prev => ({
      ...prev,
      [fieldId]: 1
    }));
  }, []);

  const renderTableField = useCallback((field, columns, rowCount) => {
    const maxRows = field.maxRows || 10;
    const minRows = field.minRows || 1;
    const currentRows = tableRowCounts[field.id] || minRows;
    const today = new Date().toISOString().split('T')[0]; // Bugünün tarihi

    return (
      <Box sx={{ width: '100%', overflowX: 'auto', mt: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2,
          bgcolor: 'background.paper',
          p: 1,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<PlusOutlined />}
              onClick={() => handleAddTableRow(field.id)}
              disabled={currentRows >= maxRows}
            >
              Satır Ekle
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => handleRemoveTableRow(field.id)}
              disabled={currentRows <= minRows}
            >
              Satır Sil
            </Button>
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={() => handleClearTable(field.id)}
              disabled={minRows === 1 && currentRows === 1}
            >
              Temizle
            </Button>
          </Stack>
          
          {field.showRowCount && (
            <Typography variant="body2" sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              px: 2,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 'medium'
            }}>
              Toplam Satır: {currentRows} / {maxRows}
            </Typography>
          )}
        </Box>

        <Table 
          size="small" 
          sx={{ 
            border: '1px solid rgba(224, 224, 224, 1)',
            borderRadius: 1,
            '& .MuiTableCell-root': {
              borderColor: 'rgba(224, 224, 224, 1)',
              padding: '8px'
            }
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              {columns.map((column, index) => (
                <TableCell 
                  key={index}
                  sx={{ 
                    color: 'white',
                    fontWeight: 'bold',
                    width: column.width || 'auto'
                  }}
                >
                  {column.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(rowCount)].map((_, rowIndex) => (
              <TableRow 
                key={rowIndex}
                sx={{
                  '&:nth-of-type(odd)': {
                    bgcolor: 'action.hover'
                  },
                  '&:hover': {
                    bgcolor: 'action.selected'
                  }
                }}
              >
                {columns.map((column, colIndex) => (
                  <TableCell 
                    key={`${rowIndex}-${colIndex}`}
                    sx={{ 
                      p: 1,
                      verticalAlign: 'top',
                      '& .MuiInputBase-root': {
                        width: '100%',
                        '& .MuiInputBase-input': {
                          whiteSpace: 'pre-wrap !important',
                          overflowWrap: 'break-word !important',
                          wordBreak: 'break-word !important',
                          overflow: 'hidden !important',
                          textOverflow: 'clip !important',
                          display: 'block !important',
                          padding: '8px !important'
                        }
                      }
                    }}
                  >
                    {(() => {
                      const fieldId = `${field.id}-${rowIndex}-${colIndex}`;
                      const currentValue = formData[field.id]?.rows?.[rowIndex]?.[colIndex] || '';
                      
                      switch (column.type) {
                        case 'text':
                        case 'email':
                        case 'phone':
                          return (
                            <TextField
                              fullWidth
                              multiline={false}
                              type={column.type === 'phone' ? 'tel' : column.type}
                              placeholder={column.placeholder || ''}
                              value={currentValue}
                              onChange={(e) => handleChange(fieldId, e.target.value)}
                              error={!!errors[fieldId]}
                              helperText={errors[fieldId] || column.helpText}
                              required={column.required}
                              variant="outlined"
                              rows={1}
                              className="form-field-input"
                              label={column.header}
                              InputProps={{
                                className: "form-field-input",
                                sx: {
                                  '& textarea': {
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    overflow: 'visible',
                                    textOverflow: 'initial',
                                    maxWidth: 'none',
                                    minHeight: '40px',
                                    height: 'auto',
                                    padding: '12px 14px',
                                    width: '100%'
                                  }
                                }
                              }}
                              sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                  height: 'auto',
                                  minHeight: '40px',
                                  width: '100%',
                                  '& textarea': {
                                    height: 'auto',
                                    minHeight: '40px',
                                    width: '100%'
                                  }
                                },
                                '& .MuiOutlinedInput-root': {
                                  height: 'auto',
                                  alignItems: 'flex-start',
                                  width: '100%'
                                }
                              }}
                            />
                          );
                        case 'date':
                          // Eğer değer boşsa bugünün tarihini kullan
                          if (!currentValue) {
                            handleChange(fieldId, today);
                          }
                          return (
                            <TextField
                              type="date"
                              size="small"
                              fullWidth
                              value={currentValue || today}
                              onChange={(e) => handleChange(fieldId, e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              className="form-field-input"
                              label={column.header}
                              InputProps={{
                                className: "form-field-input",
                                sx: {
                                  '& input': {
                                    padding: '12px',
                                    bgcolor: 'background.paper'
                                  }
                                }
                              }}
                              sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                  height: 'auto',
                                  minHeight: '40px',
                                  width: '100%',
                                  '& input': {
                                    padding: '12px',
                                    bgcolor: 'background.paper'
                                  }
                                }
                              }}
                            />
                          );
                        case 'select':
                          return (
                            <FormControl fullWidth size="small">
                              <InputLabel>{column.header}</InputLabel>
                              <Select
                                value={currentValue}
                                onChange={(e) => handleChange(fieldId, e.target.value)}
                                displayEmpty
                                variant="outlined"
                                className="form-field-input"
                                sx={{
                                  bgcolor: 'background.paper',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent'
                                  },
                                  '& .MuiSelect-select': {
                                    padding: '8px 14px'
                                  }
                                }}
                              >
                                <MenuItem value="">
                                  <em>Seçiniz</em>
                                </MenuItem>
                                {(column.options || []).map((option, optIndex) => (
                                  <MenuItem key={optIndex} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          );
                        default:
                          return null;
                      }
                    })()}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }, [formData, handleChange, handleAddTableRow, handleRemoveTableRow, handleClearTable, tableRowCounts]);

  const renderField = useCallback((field) => {
    const error = errors[field.id] || '';
    const sizeClass = field.size === '6' ? 6 : field.size === '4' ? 4 : 12;
    const rowCount = tableRowCounts[field.id] || field.minRows || 1;

    return (
      <Grid 
        item 
        xs={12} 
        sm={sizeClass} 
        md={sizeClass} 
        className="field" 
        key={field.id}
        sx={{
          mb: { xs: 2, sm: 2.5, md: 3 }
        }}
      >
        <div className="form-preview-field">
          <Typography 
            variant="subtitle1" 
            className="form-preview-field-label"
            sx={{
              fontWeight: 600,
              color: '#1e293b',
              mb: 1,
              fontSize: '14px'
            }}
          >
            {field.label}
          </Typography>
          {(() => {
            switch (field.type) {
              case 'text':
              case 'email':
              case 'phone':
                return (
                  <TextField
                    fullWidth
                    multiline={false}
                    type={field.type === 'phone' ? 'tel' : field.type}
                    placeholder={field.placeholder}
                    value={formData[field.id] || ''}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    error={!!error}
                    helperText={error || field.helpText}
                    required={field.required}
                    variant="outlined"
                    rows={1}
                    className="form-preview-input"
                    InputProps={{
                      className: "form-preview-input"
                    }}
                  />
                );

              case 'number':
                return (
                  <TextField
                    fullWidth
                    type="number"
                    placeholder={field.placeholder}
                    value={formData[field.id] || ''}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    error={!!error}
                    helperText={error || field.helpText}
                    required={field.required}
                    variant="outlined"
                    className="form-preview-input"
                    InputProps={{
                      className: "form-preview-input",
                      inputProps: {
                        min: field.min,
                        max: field.max,
                        step: field.step || 1
                      }
                    }}
                  />
                );

              case 'textarea':
                return (
                  <TextField
                    fullWidth
                    multiline
                    type="text"
                    placeholder={field.placeholder}
                    value={formData[field.id] || ''}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    error={!!error}
                    helperText={error || field.helpText}
                    required={field.required}
                    variant="outlined"
                    rows={3}
                    className="form-preview-input"
                    InputProps={{
                      className: "form-preview-input"
                    }}
                  />
                );

              case 'select':
                return (
                  <Box>
                    <FormControl fullWidth error={!!error} required={field.required} className="form-preview-select-field">
                      <Select
                        value={formData[field.id] || ''}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                      >
                        {field.options?.map((option, index) => (
                          <MenuItem key={index} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {(error || field.helpText) && (
                        <FormHelperText>{error || field.helpText}</FormHelperText>
                      )}
                    </FormControl>
                    {field.conditionalFields?.map((condition, index) => (
                      condition.option === formData[field.id] && (
                        <Box key={index} sx={{ mt: 2 }}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              mb: 1,
                              color: 'text.secondary',
                              fontWeight: 500
                            }}
                          >
                            {condition.title || `${condition.option} için açıklama`}
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={formData[`${field.id}_conditional_${index}`] || ''}
                            onChange={(e) => handleChange(`${field.id}_conditional_${index}`, e.target.value)}
                            placeholder={condition.placeholder || "Açıklama yazınız..."}
                            variant="outlined"
                            className="form-preview-input"
                          />
                        </Box>
                      )
                    ))}
                  </Box>
                );

              case 'checkbox':
                return (
                  <FormControl error={!!error} required={field.required} className="form-preview-checkbox-group">
                    <Box>
                      {field.options?.map((option, index) => (
                        <Box key={index}>
                          <label
                            className={`form-preview-checkbox-label ${formData[field.id]?.includes(option) ? 'checked' : ''}`}
                          >
                            <Checkbox
                              checked={formData[field.id]?.includes(option) || false}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...(formData[field.id] || []), option]
                                  : formData[field.id].filter(opt => opt !== option);
                                handleChange(field.id, newValue);
                              }}
                              className="form-preview-checkbox"
                            />
                            {option}
                          </label>
                          {field.conditionalFields?.map((condition, condIndex) => (
                            condition.option === option && formData[field.id]?.includes(option) && (
                              <Box key={condIndex} sx={{ ml: 4, mt: 1 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    mb: 1,
                                    color: 'text.secondary',
                                    fontWeight: 500
                                  }}
                                >
                                  {condition.title || `${condition.option} için açıklama`}
                                </Typography>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={3}
                                  value={formData[`${field.id}_conditional_${condIndex}`] || ''}
                                  onChange={(e) => handleChange(`${field.id}_conditional_${condIndex}`, e.target.value)}
                                  placeholder={condition.placeholder || "Açıklama yazınız..."}
                                  variant="outlined"
                                  className="form-preview-input"
                                />
                              </Box>
                            )
                          ))}
                        </Box>
                      ))}
                    </Box>
                    {(error || field.helpText) && (
                      <FormHelperText>{error || field.helpText}</FormHelperText>
                    )}
                  </FormControl>
                );

              case 'radio':
                return (
                  <FormControl error={!!error} required={field.required} className="form-preview-radio-group">
                    <RadioGroup
                      value={formData[field.id] || ''}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                    >
                      {field.options?.map((option, index) => (
                        <Box key={index}>
                          <label
                            className={`form-preview-radio-label ${formData[field.id] === option ? 'checked' : ''}`}
                          >
                            <Radio
                              value={option}
                              className="form-preview-radio"
                            />
                            {option}
                          </label>
                          {field.conditionalFields?.map((condition, condIndex) => (
                            condition.option === option && formData[field.id] === option && (
                              <Box key={condIndex} sx={{ ml: 4, mt: 1 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    mb: 1,
                                    color: 'text.secondary',
                                    fontWeight: 500
                                  }}
                                >
                                  {condition.title || `${condition.option} için açıklama`}
                                </Typography>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={3}
                                  value={formData[`${field.id}_conditional_${condIndex}`] || ''}
                                  onChange={(e) => handleChange(`${field.id}_conditional_${condIndex}`, e.target.value)}
                                  placeholder={condition.placeholder || "Açıklama yazınız..."}
                                  variant="outlined"
                                  className="form-preview-input"
                                />
                              </Box>
                            )
                          ))}
                        </Box>
                      ))}
                    </RadioGroup>
                    {(error || field.helpText) && (
                      <FormHelperText>{error || field.helpText}</FormHelperText>
                    )}
                  </FormControl>
                );

              case 'date':
                const today = new Date().toISOString().split('T')[0];
                
                if (!formData[field.id]) {
                  handleChange(field.id, today);
                }

                return (
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      fullWidth
                      type="date"
                      value={formData[field.id] || today}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!error}
                      helperText={error || field.helpText}
                      required={field.required}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      className="form-preview-input"
                      InputProps={{
                        className: "form-preview-input"
                      }}
                    />
                  </Box>
                );

              case 'file':
                return (
                  <FileUploadField
                    field={field}
                    value={files[field.id] || []}
                    onChange={(newFiles) => handleFileChange(field.id, newFiles, false, false)}
                    onRemove={(index) => handleRemoveFile(field.id, index)}
                    error={errors[field.id]}
                    uploading={uploading[field.id]}
                    maxFiles={MAX_FILES}
                  />
                );

              case 'image':
                return (
                  <ImageUploadField
                    field={field}
                    value={files[field.id] || []}
                    onChange={(newFiles) => handleFileChange(field.id, newFiles, true, false)}
                    onRemove={(index) => handleRemoveFile(field.id, index)}
                    error={errors[field.id]}
                    uploading={uploading[field.id]}
                    maxFiles={MAX_FILES}
                  />
                );

              case 'pdf':
                return (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
                    </Typography>
                    
                    {/* Yüklenmiş PDF'leri göster */}
                    {field.uploadedFiles && field.uploadedFiles.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        {field.uploadedFiles.map((pdfFile, index) => (
                          <Box
                            key={index}
                            sx={{
                              border: '2px solid #2196f3',
                              borderRadius: 2,
                              p: 2,
                              bgcolor: '#e3f2fd',
                              mb: 1
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FilePdfOutlined style={{ fontSize: 24, color: '#d32f2f' }} />
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {pdfFile.name}
                                  </Typography>
                                  {(field.pdfUrl || pdfFile.pdfUrl) && (
                                    <Typography variant="caption" color="success.main">
                                      ✓ Server'a yüklendi
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => {
                                  // Öncelik sırasıyla URL'leri kontrol et
                                  let pdfUrlToOpen = null;
                                  
                                  // 1. Server'dan gelen pdfUrl (en güncel)
                                  if (field.pdfUrl) {
                                    pdfUrlToOpen = field.pdfUrl;
                                  }
                                  // 2. Dosya objesindeki pdfUrl
                                  else if (pdfFile.pdfUrl) {
                                    pdfUrlToOpen = pdfFile.pdfUrl;
                                  }
                                  // 3. Lokal URL (geçici)
                                  else if (pdfFile.url) {
                                    pdfUrlToOpen = pdfFile.url;
                                  }
                                  // 4. Base64 (yedek)
                                  else if (pdfFile.base64) {
                                    pdfUrlToOpen = pdfFile.base64;
                                  }
                                  
                                  if (pdfUrlToOpen) {
                                    console.log('PDF açılıyor:', pdfUrlToOpen);
                                    window.open(pdfUrlToOpen, '_blank');
                                  } else {
                                    console.error('PDF URL bulunamadı:', field, pdfFile);
                                    alert('PDF görüntülenemiyor - URL bulunamadı');
                                  }
                                }}
                                sx={{ color: '#1976d2' }}
                              >
                                Görüntüle
                              </Button>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    <FileUploadField
                      field={field}
                      value={files[field.id] || []}
                      onChange={(newFiles) => handleFileChange(field.id, newFiles, false, true)}
                      onRemove={(index) => handleRemoveFile(field.id, index)}
                      error={errors[field.id]}
                      uploading={uploading[field.id]}
                      maxFiles={field.maxFiles || 1}
                      isPdfField={true}
                    />
                    {field.helpText && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {field.helpText}
                      </Typography>
                    )}
                  </Box>
                );

              case 'pdfDocument':
                // PDF Doküman Eki - Form oluşturan kişinin eklediği referans PDF
                if (field.uploadedFiles && field.uploadedFiles.length > 0 && field.showToUsers !== false) {
                  const pdfFile = field.uploadedFiles[0];
                  return (
                    <Box
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: '#f9f9f9'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                        <FilePdfOutlined style={{ fontSize: 20, color: '#4caf50' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                          PDF Eki
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <FilePdfOutlined style={{ fontSize: 18, color: '#d32f2f' }} />
                          <Typography variant="body2" sx={{ fontWeight: 400 }}>
                            {pdfFile.name}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => {
                              // Öncelik sırasıyla URL'leri kontrol et
                              let pdfUrlToOpen = null;
                              
                              // 1. Server'dan gelen pdfUrl (en güncel)
                              if (field.pdfUrl) {
                                pdfUrlToOpen = field.pdfUrl;
                              }
                              // 2. Dosya objesindeki pdfUrl
                              else if (pdfFile.pdfUrl) {
                                pdfUrlToOpen = pdfFile.pdfUrl;
                              }
                              // 3. Lokal URL (geçici)
                              else if (pdfFile.url) {
                                pdfUrlToOpen = pdfFile.url;
                              }
                              // 4. Base64 (yedek)
                              else if (pdfFile.base64) {
                                pdfUrlToOpen = pdfFile.base64;
                              }
                              
                              if (pdfUrlToOpen) {
                                console.log('PDF açılıyor:', pdfUrlToOpen);
                                window.open(pdfUrlToOpen, '_blank');
                              } else {
                                console.error('PDF URL bulunamadı:', field, pdfFile);
                                alert('PDF görüntülenemiyor - URL bulunamadı');
                              }
                            }}
                            sx={{ 
                              color: '#4caf50', 
                              borderColor: '#4caf50',
                              minWidth: 'auto',
                              px: 1.5
                            }}
                          >
                            Görüntüle
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = pdfFile.url || pdfFile.base64;
                              link.download = pdfFile.name;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            sx={{ 
                              bgcolor: '#4caf50',
                              minWidth: 'auto',
                              px: 1.5,
                              '&:hover': {
                                bgcolor: '#45a049'
                              }
                            }}
                          >
                            İndir
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                } else {
                  return (
                    <Box
                      sx={{
                        border: '1px dashed #ccc',
                        borderRadius: 1,
                        p: 2,
                        textAlign: 'center',
                        bgcolor: '#f9f9f9',
                        color: '#666'
                      }}
                    >
                      <FilePdfOutlined style={{ fontSize: 24, color: '#ccc', marginBottom: 8 }} />
                      <Typography variant="body2" color="text.secondary">
                        PDF eklenmedi
                      </Typography>
                    </Box>
                  );
                }

              case 'signature':
                return (
                  <Box className="form-preview-signature-field">
                    <Typography variant="subtitle1" className="form-preview-field-label">
                      İmza
                    </Typography>
                    <ReactSignatureCanvas
                      ref={ref => signatureRefs.current[field.id] = ref}
                      canvasProps={{
                        className: 'form-preview-signature-canvas'
                      }}
                      onEnd={() => handleSignatureEnd(field.id)}
                    />
                    <Box className="form-preview-signature-buttons">
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (signatureRefs.current[field.id]) {
                            const data = signatureRefs.current[field.id].getTrimmedCanvas().toDataURL('image/png');
                            setSignatureData(prev => ({
                              ...prev,
                              [field.id]: data
                            }));
                            setFormData(prev => ({
                              ...prev,
                              [field.id]: data
                            }));
                          }
                        }}
                      >
                        İmza Kaydet
                      </Button>
                      {signatureData[field.id] && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            if (signatureRefs.current[field.id]) {
                              signatureRefs.current[field.id].clear();
                              setSignatureData(prev => {
                                const newData = { ...prev };
                                delete newData[field.id];
                                return newData;
                              });
                              setFormData(prev => {
                                const newData = { ...prev };
                                delete newData[field.id];
                                return newData;
                              });
                            }
                          }}
                        >
                          İmza Sil
                        </Button>
                      )}
                    </Box>
                    {signatureData[field.id] && (
                      <Box sx={{ mt: 2 }}>
                        <img
                          src={signatureData[field.id]}
                          alt="İmza"
                          style={{
                            maxWidth: '200px',
                            maxHeight: '100px',
                            objectFit: 'contain'
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                );

              case 'name':
                return (
                  <Box className="form-preview-name-field">
                    <TextField
                      label="Ad"
                      value={formData[`${field.id}_firstName`] || ''}
                      onChange={(e) => {
                        const newFirstName = e.target.value;
                        const currentLastName = formData[`${field.id}_lastName`] || '';
                        handleChange(`${field.id}_firstName`, newFirstName);
                        handleChange(field.id, {
                          type: 'name',
                          firstName: newFirstName,
                          lastName: currentLastName,
                          value: `${newFirstName} ${currentLastName}`.trim()
                        });
                      }}
                      error={!!errors[`${field.id}_firstName`]}
                      helperText={errors[`${field.id}_firstName`]}
                      fullWidth
                      variant="outlined"
                      required={field.required}
                      className="form-preview-input"
                    />
                    <TextField
                      label="Soyad"
                      value={formData[`${field.id}_lastName`] || ''}
                      onChange={(e) => {
                        const newLastName = e.target.value;
                        const currentFirstName = formData[`${field.id}_firstName`] || '';
                        handleChange(`${field.id}_lastName`, newLastName);
                        handleChange(field.id, {
                          type: 'name',
                          firstName: currentFirstName,
                          lastName: newLastName,
                          value: `${currentFirstName} ${newLastName}`.trim()
                        });
                      }}
                      error={!!errors[`${field.id}_lastName`]}
                      helperText={errors[`${field.id}_lastName`]}
                      fullWidth
                      variant="outlined"
                      required={field.required}
                      className="form-preview-input"
                    />
                  </Box>
                );

              case 'time':
                const now = new Date();
                const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                
                if (!formData[field.id]) {
                  handleChange(field.id, currentTime);
                }

                return (
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      fullWidth
                      type="time"
                      value={formData[field.id] || currentTime}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!error}
                      helperText={error || field.helpText}
                      required={field.required}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      className="form-preview-input"
                      InputProps={{
                        className: "form-preview-input"
                      }}
                    />
                  </Box>
                );

              case 'table':
                const columns = field.columns?.length > 0 ? field.columns : defaultColumns;
                return renderTableField(field, columns, rowCount);

              case 'calculation':
                return (
                  <CalculationField
                    field={field}
                    value={calculationValues[field.id]}
                    onChange={(value) => handleChange(field.id, value)}
                    formData={formData}
                  />
                );

              case 'address':
                return (
                  <Box
                    sx={{
                      border: '2px solid #0097a7',
                      borderRadius: 3,
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      bgcolor: '#e0f7fa',
                      color: '#0097a7',
                      minHeight: 120,
                      mb: 1,
                      boxShadow: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <span style={{ fontSize: 32, color: '#0097a7', marginRight: 8 }}>📍</span>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Adres Bilgisi
                      </Typography>
                    </Box>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="İl"
                        value={formData[`${field.id}_il`] || ''}
                        onChange={e => handleChange(`${field.id}_il`, e.target.value)}
                        variant="outlined"
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="İlçe"
                        value={formData[`${field.id}_ilce`] || ''}
                        onChange={e => handleChange(`${field.id}_ilce`, e.target.value)}
                        variant="outlined"
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Mahalle"
                        value={formData[`${field.id}_mahalle`] || ''}
                        onChange={e => handleChange(`${field.id}_mahalle`, e.target.value)}
                        variant="outlined"
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Sokak"
                        value={formData[`${field.id}_sokak`] || ''}
                        onChange={e => handleChange(`${field.id}_sokak`, e.target.value)}
                        variant="outlined"
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Bina No"
                          value={formData[`${field.id}_binaNo`] || ''}
                          onChange={e => handleChange(`${field.id}_binaNo`, e.target.value)}
                          variant="outlined"
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Daire No"
                          value={formData[`${field.id}_daireNo`] || ''}
                          onChange={e => handleChange(`${field.id}_daireNo`, e.target.value)}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Box>
                );

              // Etkileşimli Alanlar
              case 'toggle':
                return (
                  <Box 
                    className="form-preview-toggle-field"
                    sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#f8fafc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {field.toggleLabel || "Seçeneği Aktif Et"}
                    </Typography>
                    <Switch
                      checked={formData[field.id] || false}
                      onChange={(e) => handleChange(field.id, e.target.checked)}
                      color="primary"
                      size="medium"
                    />
                  </Box>
                );

              case 'slider':
                const sliderValue = formData[field.id] || field.defaultValue || field.min || 0;
                return (
                  <Box 
                    className="form-preview-slider-field"
                    sx={{
                      p: 3,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#fff'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {field.min || 0}
                      </Typography>
                      <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                        {sliderValue}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {field.max || 100}
                      </Typography>
                    </Box>
                    <Slider
                      value={sliderValue}
                      min={field.min || 0}
                      max={field.max || 100}
                      step={field.step || 1}
                      onChange={(e, newValue) => handleChange(field.id, newValue)}
                      marks={field.marks}
                      valueLabelDisplay="auto"
                      color="primary"
                      sx={{
                        '& .MuiSlider-thumb': {
                          width: 20,
                          height: 20,
                        },
                        '& .MuiSlider-track': {
                          height: 6,
                        },
                        '& .MuiSlider-rail': {
                          height: 6,
                        }
                      }}
                    />
                  </Box>
                );

              case 'range-slider':
                const rangeValue = formData[field.id] || field.defaultValue || [20, 80];
                return (
                  <Box 
                    className="form-preview-range-slider-field"
                    sx={{
                      p: 3,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#fff'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Min: {field.min || 0}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Chip 
                          label={`${rangeValue[0]}`} 
                          color="primary" 
                          variant="outlined" 
                          size="small"
                        />
                        <Typography variant="body2" color="text.secondary">-</Typography>
                        <Chip 
                          label={`${rangeValue[1]}`} 
                          color="primary" 
                          variant="outlined" 
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Max: {field.max || 100}
                      </Typography>
                    </Box>
                    <Slider
                      value={rangeValue}
                      min={field.min || 0}
                      max={field.max || 100}
                      step={field.step || 1}
                      onChange={(e, newValue) => handleChange(field.id, newValue)}
                      valueLabelDisplay="auto"
                      color="primary"
                      sx={{
                        '& .MuiSlider-thumb': {
                          width: 20,
                          height: 20,
                        },
                        '& .MuiSlider-track': {
                          height: 6,
                        },
                        '& .MuiSlider-rail': {
                          height: 6,
                        }
                      }}
                    />
                  </Box>
                );

              case 'rating':
                return (
                  <Box 
                    className="form-preview-rating-field"
                    sx={{
                      p: 3,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#fff',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                      Değerlendirmenizi yapın
                    </Typography>
                    <Rating
                      value={formData[field.id] || 0}
                      onChange={(e, newValue) => handleChange(field.id, newValue)}
                      max={field.max || 5}
                      size="large"
                      precision={field.allowHalf ? 0.5 : 1}
                      sx={{
                        fontSize: '2rem',
                        '& .MuiRating-iconFilled': {
                          color: '#ff6d75',
                        },
                        '& .MuiRating-iconHover': {
                          color: '#ff3d47',
                        },
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {formData[field.id] || 0} / {field.max || 5} yıldız
                    </Typography>
                  </Box>
                );

              case 'color-picker':
                return (
                  <Box 
                    className="form-preview-color-picker-field"
                    sx={{
                      p: 3,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#fff'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          border: '2px solid #e0e0e0',
                          bgcolor: formData[field.id] || field.defaultValue || '#1976d2',
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <input
                          type="color"
                          value={formData[field.id] || field.defaultValue || '#1976d2'}
                          onChange={(e) => handleChange(field.id, e.target.value)}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            cursor: 'pointer',
                            opacity: 0
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Seçilen Renk
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          fontFamily: 'monospace',
                          bgcolor: '#f5f5f5',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          mt: 0.5
                        }}>
                          {formData[field.id] || field.defaultValue || '#1976d2'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );

              case 'currency':
                return (
                  <Box 
                    className="form-preview-currency-field"
                    sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#fff'
                    }}
                  >
                    <TextField
                      fullWidth
                      type="number"
                      placeholder={field.placeholder || "0.00"}
                      value={formData[field.id] || ''}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!error}
                      helperText={error || field.helpText}
                      required={field.required}
                      variant="outlined"
                      className="form-preview-input"
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            bgcolor: '#f5f5f5',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            mr: 1,
                            border: '1px solid #e0e0e0'
                          }}>
                            <Typography sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {field.currency || 'TRY'}
                            </Typography>
                          </Box>
                        ),
                        className: "form-preview-input",
                        inputProps: {
                          min: field.min || 0,
                          step: "0.01"
                        }
                      }}
                    />
                  </Box>
                );

              case 'url':
                return (
                  <Box 
                    className="form-preview-url-field"
                    sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#fff'
                    }}
                  >
                    <TextField
                      fullWidth
                      type="url"
                      placeholder={field.placeholder || "https://example.com"}
                      value={formData[field.id] || ''}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!error}
                      helperText={error || field.helpText}
                      required={field.required}
                      variant="outlined"
                      className="form-preview-input"
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ color: 'text.secondary', mr: 1 }}>
                            🌐
                          </Typography>
                        ),
                        className: "form-preview-input"
                      }}
                    />
                  </Box>
                );

              // Sosyal Medya Alanları
              case 'facebook':
                return (
                  <Box 
                    className="form-preview-social-field"
                    sx={{
                      p: 2,
                      border: '1px solid #1877f2',
                      borderRadius: 2,
                      bgcolor: '#f0f2f5'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: '#1877f2', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1
                      }}>
                        <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>f</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1877f2' }}>
                        Facebook Profili
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      type="url"
                      placeholder="https://facebook.com/kullaniciadi"
                      value={formData[field.id] || ''}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!error}
                      helperText={error || field.helpText}
                      required={field.required}
                      variant="outlined"
                      size="small"
                      className="form-preview-input"
                    />
                  </Box>
                );

              case 'twitter':
                return (
                  <Box 
                    className="form-preview-social-field"
                    sx={{
                      p: 2,
                      border: '1px solid #1da1f2',
                      borderRadius: 2,
                      bgcolor: '#f7fafe'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: '#1da1f2', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1
                      }}>
                        <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>X</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1da1f2' }}>
                        Twitter/X Profili
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      type="url"
                      placeholder="https://twitter.com/kullaniciadi"
                      value={formData[field.id] || ''}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!error}
                      helperText={error || field.helpText}
                      required={field.required}
                      variant="outlined"
                      size="small"
                      className="form-preview-input"
                    />
                  </Box>
                );

              case 'instagram':
                return (
                  <Box 
                    className="form-preview-social-field"
                    sx={{
                      p: 2,
                      border: '1px solid #e4405f',
                      borderRadius: 2,
                      bgcolor: '#fef7f8'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)', 
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1
                      }}>
                        <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>📷</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#e4405f' }}>
                        Instagram Profili
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      type="url"
                      placeholder="https://instagram.com/kullaniciadi"
                      value={formData[field.id] || ''}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!error}
                      helperText={error || field.helpText}
                      required={field.required}
                      variant="outlined"
                      size="small"
                      className="form-preview-input"
                    />
                  </Box>
                );

              case 'linkedin':
                return (
                  <Box 
                    className="form-preview-social-field"
                    sx={{
                      p: 2,
                      border: '1px solid #0077b5',
                      borderRadius: 2,
                      bgcolor: '#f3f9fc'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: '#0077b5', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1
                      }}>
                        <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>in</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#0077b5' }}>
                        LinkedIn Profili
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      type="url"
                      placeholder="https://linkedin.com/in/kullaniciadi"
                      value={formData[field.id] || ''}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!error}
                      helperText={error || field.helpText}
                      required={field.required}
                      variant="outlined"
                      size="small"
                      className="form-preview-input"
                    />
                  </Box>
                );

              case 'youtube':
                return (
                  <Box 
                    className="form-preview-social-field"
                    sx={{
                      p: 2,
                      border: '1px solid #ff0000',
                      borderRadius: 2,
                      bgcolor: '#fffafa'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: '#ff0000', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1
                      }}>
                        <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>▶</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#ff0000' }}>
                        YouTube Kanalı
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      type="url"
                      placeholder="https://youtube.com/channel/kanalid"
                      value={formData[field.id] || ''}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!error}
                      helperText={error || field.helpText}
                      required={field.required}
                      variant="outlined"
                      size="small"
                      className="form-preview-input"
                    />
                  </Box>
                );

              case 'whatsapp':
                return (
                  <Box 
                    className="form-preview-social-field"
                    sx={{
                      p: 2,
                      border: '1px solid #25d366',
                      borderRadius: 2,
                      bgcolor: '#f0fcf4'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: '#25d366', 
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1
                      }}>
                        <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>📞</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#25d366' }}>
                        WhatsApp Numarası
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      type="tel"
                      placeholder="+90 555 123 45 67"
                      value={formData[field.id] || ''}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      error={!!error}
                      helperText={error || field.helpText}
                      required={field.required}
                      variant="outlined"
                      size="small"
                      className="form-preview-input"
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ color: '#25d366', mr: 1, fontWeight: 500 }}>
                            {field.countryCode || '+90'}
                          </Typography>
                        ),
                      }}
                    />
                  </Box>
                );

              case 'social-links':
                const socialPlatforms = field.platforms || ['facebook', 'twitter', 'instagram', 'linkedin'];
                return (
                  <Box 
                    className="form-preview-social-links-field"
                    sx={{
                      p: 3,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#fff'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                      🌐 Sosyal Medya Bağlantıları
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {socialPlatforms.map((platform) => {
                        const platformColors = {
                          facebook: '#1877f2',
                          twitter: '#1da1f2',
                          instagram: '#e4405f',
                          linkedin: '#0077b5',
                          youtube: '#ff0000'
                        };
                        return (
                          <Box key={platform} sx={{ 
                            p: 1.5, 
                            border: '1px solid #f0f0f0', 
                            borderRadius: 1,
                            bgcolor: '#fafafa'
                          }}>
                            <TextField
                              fullWidth
                              type="url"
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{
                                    width: 16,
                                    height: 16,
                                    bgcolor: platformColors[platform],
                                    borderRadius: '2px'
                                  }} />
                                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                                </Box>
                              }
                              placeholder={`${platform} URL'nizi girin`}
                              value={formData[`${field.id}_${platform}`] || ''}
                              onChange={(e) => handleChange(`${field.id}_${platform}`, e.target.value)}
                              variant="outlined"
                              size="small"
                              className="form-preview-input"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': {
                                    borderColor: platformColors[platform],
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: platformColors[platform],
                                  },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: platformColors[platform],
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );

              // Görsel Alanlar
              case 'drawing':
                return (
                  <Box 
                    className="form-preview-drawing-field"
                    sx={{
                      p: 3,
                      border: '2px dashed #9c27b0',
                      borderRadius: 2,
                      bgcolor: '#faf5ff'
                    }}
                  >
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ color: '#9c27b0', fontWeight: 600, mb: 1 }}>
                        🎨 Çizim Alanı
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Bu özellik form doldurulurken aktif olacak
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 200,
                        border: '2px solid #e0e0e0',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#ffffff',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Box sx={{ 
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        display: 'flex',
                        gap: 1,
                        zIndex: 1
                      }}>
                        {['🖊️', '✏️', '🖍️', '🖌️'].map((tool, index) => (
                          <Chip 
                            key={index}
                            label={tool} 
                            size="small" 
                            sx={{ 
                              bgcolor: 'rgba(156, 39, 176, 0.1)',
                              color: '#9c27b0'
                            }} 
                          />
                        ))}
                      </Box>
                      <Typography variant="h6" color="text.secondary" sx={{ 
                        textAlign: 'center',
                        opacity: 0.7,
                        fontSize: '3rem'
                      }}>
                        🎨
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Çizim araçları burada görünecek
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Button variant="outlined" size="small" sx={{ color: '#9c27b0', borderColor: '#9c27b0' }}>
                        Temizle
                      </Button>
                      <Button variant="contained" size="small" sx={{ bgcolor: '#9c27b0' }}>
                        Kaydet
                      </Button>
                    </Box>
                  </Box>
                );

              case 'qr-code':
                return (
                  <Box 
                    className="form-preview-qr-code-field"
                    sx={{
                      p: 3,
                      border: '2px dashed #673ab7',
                      borderRadius: 2,
                      bgcolor: '#f3e5f5'
                    }}
                  >
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ color: '#673ab7', fontWeight: 600, mb: 1 }}>
                        📱 QR Kod Okuyucu
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Kamera ile QR kod okuma özelliği
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 200,
                        border: '2px solid #e0e0e0',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#ffffff',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Box sx={{
                        width: 120,
                        height: 120,
                        border: '3px solid #673ab7',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        position: 'relative'
                      }}>
                        <Typography variant="h4" sx={{ color: '#673ab7' }}>
                          ⚫⚪⚫
                        </Typography>
                        <Box sx={{
                          position: 'absolute',
                          top: -2,
                          left: -2,
                          width: 20,
                          height: 20,
                          border: '3px solid #673ab7',
                          borderRight: 'none',
                          borderBottom: 'none'
                        }} />
                        <Box sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 20,
                          height: 20,
                          border: '3px solid #673ab7',
                          borderLeft: 'none',
                          borderBottom: 'none'
                        }} />
                        <Box sx={{
                          position: 'absolute',
                          bottom: -2,
                          left: -2,
                          width: 20,
                          height: 20,
                          border: '3px solid #673ab7',
                          borderRight: 'none',
                          borderTop: 'none'
                        }} />
                        <Box sx={{
                          position: 'absolute',
                          bottom: -2,
                          right: -2,
                          width: 20,
                          height: 20,
                          border: '3px solid #673ab7',
                          borderLeft: 'none',
                          borderTop: 'none'
                        }} />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        QR kodu kamera ile okuyun
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Button variant="outlined" size="small" sx={{ color: '#673ab7', borderColor: '#673ab7' }}>
                        Kamerayı Aç
                      </Button>
                      <Button variant="contained" size="small" sx={{ bgcolor: '#673ab7' }}>
                        Sonucu Kaydet
                      </Button>
                    </Box>
                    {formData[field.id] && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                          Okunan QR: {formData[field.id]}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );

              case 'gauge':
                const gaugeValue = formData[field.id] || field.value || 50;
                const gaugeMin = field.min || 0;
                const gaugeMax = field.max || 100;
                const gaugePercentage = ((gaugeValue - gaugeMin) / (gaugeMax - gaugeMin)) * 100;
                
                return (
                  <Box 
                    className="form-preview-gauge-field"
                    sx={{
                      p: 3,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#fff',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                      📊 Gösterge
                    </Typography>
                    <Box sx={{ 
                      position: 'relative', 
                      width: 200, 
                      height: 100, 
                      mx: 'auto', 
                      mb: 2 
                    }}>
                      <Box sx={{
                        width: 200,
                        height: 100,
                        borderRadius: '100px 100px 0 0',
                        border: '12px solid #f0f0f0',
                        borderBottom: 'none',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <Box sx={{
                          position: 'absolute',
                          top: -12,
                          left: -12,
                          width: 224,
                          height: 112,
                          borderRadius: '112px 112px 0 0',
                          background: `conic-gradient(from 180deg, #673ab7 0deg, #673ab7 ${gaugePercentage * 1.8}deg, transparent ${gaugePercentage * 1.8}deg)`,
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            width: 200,
                            height: 100,
                            borderRadius: '100px 100px 0 0',
                            bgcolor: 'white'
                          }
                        }} />
                      </Box>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontWeight: 700,
                          color: '#673ab7'
                        }}
                      >
                        {gaugeValue}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Aralık: {gaugeMin} - {gaugeMax}
                    </Typography>
                  </Box>
                );

              case 'progress':
                const progressValue = formData[field.id] || field.value || 0;
                const progressMin = field.min || 0;
                const progressMax = field.max || 100;
                const progressPercentage = ((progressValue - progressMin) / (progressMax - progressMin)) * 100;
                
                return (
                  <Box 
                    className="form-preview-progress-field"
                    sx={{
                      p: 3,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#fff'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        📈 İlerleme
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#8bc34a' }}>
                        {Math.round(progressPercentage)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progressPercentage}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        bgcolor: '#f0f0f0',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 6,
                          bgcolor: '#8bc34a'
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {progressMin}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {progressValue} / {progressMax}
                      </Typography>
                    </Box>
                  </Box>
                );

              // Sürdürülebilirlik Alanları
              case 'carbon-calculator':
                const paperSaved = Math.floor(Math.random() * 1000) + 500; // Simüle edilen değer
                const treesSaved = Math.floor(paperSaved / 20); // 20 sayfa = 1 ağaç
                const co2Reduced = Math.floor(paperSaved * 0.01); // kg CO2
                const energySaved = Math.floor(paperSaved * 0.05); // kWh
                
                return (
                  <Box 
                    className="form-preview-carbon-calculator"
                    sx={{
                      p: 4,
                      background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                      borderRadius: 3,
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.1)'
                    }} />
                    
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, textAlign: 'center' }}>
                      🌱 FormClicker Karbon Ayak İzi Hesaplayıcı
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                            📄 {paperSaved.toLocaleString()}
                          </Typography>
                          <Typography variant="body1">
                            Sayfa Tasarrufu
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                            🌳 {treesSaved}
                          </Typography>
                          <Typography variant="body1">
                            Ağaç Koruması
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                            🌍 {co2Reduced}kg
                          </Typography>
                          <Typography variant="body1">
                            CO2 Azaltımı
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                            ⚡ {energySaved}kWh
                          </Typography>
                          <Typography variant="body1">
                            Enerji Tasarrufu
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                        FormClicker ile dijital dönüşümünüz çevreye olan katkınız!
                      </Typography>
                      <Button 
                        variant="contained" 
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)', 
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                        }}
                      >
                        📊 Detaylı Rapor Al
                      </Button>
                    </Box>
                  </Box>
                );

              case 'eco-impact-display':
                const currentPaperSaved = Math.floor(Math.random() * 5000) + 2000;
                const targetPaperSaved = 10000;
                const progressPaper = (currentPaperSaved / targetPaperSaved) * 100;
                
                return (
                  <Box 
                    className="form-preview-eco-impact"
                    sx={{
                      p: 3,
                      border: '2px solid #4CAF50',
                      borderRadius: 3,
                      bgcolor: '#f1f8e9',
                      position: 'relative'
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 3, color: '#2E7D32', fontWeight: 700, textAlign: 'center' }}>
                      🌿 Çevresel Etki Göstergesi
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 700 }}>
                            📄 {currentPaperSaved.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Kağıt Tasarrufu
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={progressPaper} 
                            sx={{ 
                              mt: 1, 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: 'rgba(76,175,80,0.2)',
                              '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50' }
                            }} 
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 700 }}>
                            🌳 {Math.floor(currentPaperSaved / 20)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Korunan Ağaç
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={progressPaper} 
                            sx={{ 
                              mt: 1, 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: 'rgba(76,175,80,0.2)',
                              '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50' }
                            }} 
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 700 }}>
                            🌍 {Math.floor(currentPaperSaved * 0.01)}kg
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            CO2 Azaltımı
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={progressPaper} 
                            sx={{ 
                              mt: 1, 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: 'rgba(76,175,80,0.2)',
                              '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50' }
                            }} 
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Chip 
                        label={`🎯 Hedef: ${progressPaper.toFixed(1)}% tamamlandı`}
                        color="success"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                );

              case 'sustainability-score':
                const sustainabilityScore = 85; // Simüle edilen skor
                const scoreColor = sustainabilityScore >= 80 ? '#4CAF50' : sustainabilityScore >= 60 ? '#FF9800' : '#F44336';
                
                return (
                  <Box 
                    className="form-preview-sustainability-score"
                    sx={{
                      p: 3,
                      border: `2px solid ${scoreColor}`,
                      borderRadius: 3,
                      bgcolor: '#fff',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 3, color: scoreColor, fontWeight: 700 }}>
                      ♻️ Sürdürülebilirlik Skoru
                    </Typography>
                    
                    <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
                      <Box sx={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        background: `conic-gradient(${scoreColor} 0deg, ${scoreColor} ${sustainabilityScore * 3.6}deg, #e0e0e0 ${sustainabilityScore * 3.6}deg)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Box sx={{
                          width: 90,
                          height: 90,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column'
                        }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: scoreColor }}>
                            {sustainabilityScore}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            /100
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h6" sx={{ color: '#4CAF50' }}>40%</Typography>
                        <Typography variant="caption">Dijital Kullanım</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h6" sx={{ color: '#2196F3' }}>30%</Typography>
                        <Typography variant="caption">Kağıt Azaltma</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h6" sx={{ color: '#FF9800' }}>20%</Typography>
                        <Typography variant="caption">Verimlilik</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h6" sx={{ color: '#9C27B0' }}>10%</Typography>
                        <Typography variant="caption">Yenilik</Typography>
                      </Box>
                    </Box>
                    
                    <Button variant="outlined" color="success" size="small">
                      💡 İyileştirme Önerileri
                    </Button>
                  </Box>
                );

              case 'green-certificate':
                return (
                  <Box 
                    className="form-preview-green-certificate"
                    sx={{
                      p: 4,
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      border: '3px solid #4CAF50',
                      borderRadius: 3,
                      position: 'relative'
                    }}
                  >
                    <Box sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      width: 60,
                      height: 60,
                      background: 'linear-gradient(45deg, #4CAF50, #2E7D32)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="h4">🏆</Typography>
                    </Box>
                    
                    <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, color: '#2E7D32', textAlign: 'center' }}>
                      🌱 FormClicker Yeşil İşletme Sertifikası
                    </Typography>
                    
                    <Typography variant="h6" sx={{ mb: 3, color: '#4CAF50', textAlign: 'center' }}>
                      Sürdürülebilir Dijital Dönüşüm Belgesi
                    </Typography>
                    
                    <Box sx={{ 
                      bgcolor: 'white', 
                      p: 3, 
                      borderRadius: 2, 
                      border: '1px solid #e0e0e0',
                      mb: 3
                    }}>
                      <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Bu sertifika, <strong>[Şirket Adı]</strong> şirketinin FormClicker platformunu kullanarak
                        çevresel sürdürülebilirliğe yaptığı katkıyı onaylar.
                      </Typography>
                      
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            📄 Kağıt Tasarrufu: <strong>2,450 sayfa</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            🌳 Ağaç Koruması: <strong>12 ağaç</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            🌍 CO2 Azaltımı: <strong>24 kg</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            📅 Geçerlilik: <strong>365 gün</strong>
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Sertifika No: FC-2024-{Math.floor(Math.random() * 10000)}
                        </Typography>
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          Tarih: {new Date().toLocaleDateString('tr-TR')}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{
                          width: 60,
                          height: 60,
                          border: '2px solid #4CAF50',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1
                        }}>
                          <Typography variant="h6">📱</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          QR Doğrulama
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Button variant="contained" color="success" size="small" sx={{ mr: 1 }}>
                        📥 PDF İndir
                      </Button>
                      <Button variant="outlined" color="success" size="small">
                        🔗 Paylaş
                      </Button>
                    </Box>
                  </Box>
                );

              default:
                return null;
            }
          })()}
        </div>
      </Grid>
    );
  }, [formData, errors, settings, handleChange, files, uploading, handleFileChange, handleRemoveFile, signatureData, tableRowCounts, handleAddTableRow, handleRemoveTableRow, handleClearTable]);

  const getRevisionText = () => {
    if (!settings?.revisionEnabled) return '';
    
    const format = settings?.revisionFormat || 'REV-{number}';
    const number = settings?.revisionNumber || '1';
    const date = settings?.revisionDate ? 
      new Date(settings.revisionDate).toLocaleDateString('tr-TR') : 
      new Date().toLocaleDateString('tr-TR');
    
    return format
      .replace('{number}', number)
      .replace('{date}', date);
  };

  useEffect(() => {
    console.log('Gelen settings:', settings); // Settings'i kontrol et
  }, [settings]);

  if (submitted) {
  return (
      <Container 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          bgcolor: '#f8fafc'
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '600px',
            p: 4,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}
        >
          {logoUrl && (
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  maxWidth: '200px',
                  height: 'auto',
                  marginBottom: '20px'
                }}
              />
            </Box>
          )}
          <Result
            status="success"
            icon={
              <CheckCircleOutlined
                style={{
                  fontSize: '120px',
                  color: '#52c41a',
                  marginBottom: '24px',
                  filter: 'drop-shadow(0 4px 8px rgba(82, 196, 26, 0.3))'
                }}
              />
            }
            title={
              <Typography
                sx={{
                  fontSize: '28px',
                  fontWeight: 600,
                  color: '#1e293b',
                  mb: 2,
                  mt: 2
                }}
              >
                Form Başarıyla Gönderildi!
              </Typography>
            }
            subTitle={
              <Box sx={{ mt: 3 }}>
                <Typography 
                  sx={{ 
                    color: '#64748b',
                    mb: 1.5,
                    fontSize: '18px'
                  }}
                >
                  Form ID: {formId}
                </Typography>
                <Typography 
                  sx={{ 
                    color: '#64748b',
                    fontSize: '18px'
                  }}
                >
                  {formTitle}
                </Typography>
              </Box>
            }
            style={{
              padding: '32px'
            }}
          />
        </Box>
      </Container>
    );
  }

  return (
    <>
    <Container 
      className="form-preview-container"
      sx={{
        px: { xs: 1, sm: 2, md: 3 },
        width: '100%',
        maxWidth: '100% !important',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        bgcolor: settings?.backgroundColor || '#f8fafc',
        minHeight: '100vh',
        fontFamily: settings?.fontFamily || 'inherit',
        fontSize: settings?.fontSize === 'small' ? '14px' : settings?.fontSize === 'large' ? '18px' : '16px',
      }}
    >
      <Paper 
        className="form-preview-paper"
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          width: '100%',
          maxWidth: {
            xs: '100%',
            sm: '600px',
            md: '700px',
            lg: '800px'
          },
          bgcolor: settings?.backgroundColor || '#ffffff',
          borderRadius: { xs: 1, sm: 2, md: 3 },
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          border: '1px solid #e2e8f0',
          position: 'relative',
          overflow: 'auto',
          fontFamily: settings?.fontFamily || 'inherit',
          fontSize: settings?.fontSize === 'small' ? '14px' : settings?.fontSize === 'large' ? '18px' : '16px',
        }}
      >
        {/* Logo Alanı */}
        {logoUrl && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
              width: '100%'
            }}
          >
            <Box
              component="img"
              src={logoUrl}
              alt="Form logosu"
              sx={{
                maxWidth: '200px',
                maxHeight: '100px',
                objectFit: 'contain',
                borderRadius: 1,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            />
          </Box>
        )}

        <Grid 
          container 
          spacing={2}
          className="form-preview-grid"
          sx={{
            width: '100%',
            margin: 0,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            fontFamily: settings?.fontFamily || 'inherit',
            fontSize: settings?.fontSize === 'small' ? '14px' : settings?.fontSize === 'large' ? '18px' : '16px',
          }}
        >
          <Grid item xs={12} className="form-preview-grid-item">
            <Typography 
              variant="h4" 
              className="form-preview-title"
              sx={{
                fontSize: { xs: '22px', sm: '24px', md: '28px' },
                fontWeight: 700,
                color: settings?.titleColor || '#1e293b',
                mb: { xs: 1, sm: 1.5, md: 2 },
                textAlign: 'center',
                width: '100%',
                wordWrap: 'break-word',
                lineHeight: 1.3,
                letterSpacing: '-0.5px',
                fontFamily: settings?.fontFamily || 'inherit',
              }}
            >
              {formTitle}
            </Typography>
            
            <Typography 
              variant="body1" 
              className="form-preview-description"
              sx={{
                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                color: settings?.descriptionColor || '#64748b',
                mb: { xs: 2, sm: 2.5, md: 3 },
                textAlign: 'center',
                width: '100%',
                wordWrap: 'break-word',
                lineHeight: 1.6,
                fontFamily: settings?.fontFamily || 'inherit',
              }}
            >
              {formDescription}
            </Typography>
          </Grid>
          {currentFields.map((field, index) => (
            <Grid 
              key={field.id || index}
              item 
              xs={field.size === '6' ? 6 : field.size === '4' ? 4 : 12}
              sm={field.size === '6' ? 6 : field.size === '4' ? 4 : 12}
              md={field.size === '6' ? 6 : field.size === '4' ? 4 : 12}
              className="form-preview-grid-item"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 1,
                '& .form-preview-field': {
                  width: '100%',
                  height: '100%',
                  '& .MuiFormControl-root': {
                    width: '100%'
                  },
                  '& .MuiInputBase-root': {
                    minHeight: settings?.fieldSize === 'small' ? '36px' : settings?.fieldSize === 'large' ? '56px' : '45px',
                    fontSize: settings?.fontSize === 'small' ? '14px' : settings?.fontSize === 'large' ? '18px' : '16px',
                    fontFamily: settings?.fontFamily || 'inherit',
                    borderRadius: settings?.fieldBorderRadius || '4px',
                    borderColor: settings?.fieldBorderColor || '#e0e0e0',
                    bgcolor: settings?.fieldBackgroundColor || '#fff',
                    '&:focus-within': {
                      borderColor: settings?.fieldFocusColor || '#1976d2',
                      boxShadow: settings?.fieldFocusColor ? `0 0 0 2px ${settings.fieldFocusColor}33` : undefined
                    }
                  },
                  '& .form-preview-field-label': {
                    fontSize: '15px',
                    marginBottom: '8px',
                    fontFamily: settings?.fontFamily || 'inherit',
                  }
                }
              }}
            >
              <div className="form-preview-field">
                {renderField(field)}
              </div>
            </Grid>
          ))}
          {/* Progress Bar */}
        {/*   <Box sx={{ width: '100%', mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={Math.round((currentPage / maxPage) * 100)}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: '#e3eafc',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  backgroundColor: '#1976d2'
                }
              }}
            />
            <Typography align="center" sx={{ mt: 1, fontWeight: 500 }}>
              {`Adım ${currentPage} / ${maxPage}`}
            </Typography>
          </Box> */}
          {/* Sayfa Yönetimi Butonları - Sadece birden fazla sayfa varsa göster */}
          {maxPage > 1 && (
            <Grid item xs={12} sx={{ mt: 3, mb: 2 }}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}
              >
                {/* Progress Bar */}
                <Box sx={{ mb: 3 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.round((currentPage / maxPage) * 100)}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 6,
                        background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)'
                      }
                    }}
                  />
                  <Typography align="center" sx={{ mt: 1, fontWeight: 600, fontSize: '1.1rem' }}>
                    {`Sayfa ${currentPage} / ${maxPage}`}
                  </Typography>
                </Box>

                {/* Navigasyon butonları */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center'
                }}>
                  <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    sx={{ 
                      minWidth: 120, 
                      fontWeight: 600, 
                      borderRadius: 3, 
                      fontSize: 16, 
                      py: 1.5, 
                      px: 3,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.25)'
                      },
                      '&:disabled': {
                        bgcolor: 'rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.5)'
                      }
                    }}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Geri
                  </Button>

                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)',
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    <Typography sx={{ 
                      fontWeight: 700, 
                      fontSize: 18,
                      textAlign: 'center'
                    }}>
                      {`${currentPage} / ${maxPage}`}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      minWidth: 120, 
                      fontWeight: 600, 
                      borderRadius: 3, 
                      fontSize: 16, 
                      py: 1.5, 
                      px: 3,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.25)'
                      },
                      '&:disabled': {
                        bgcolor: 'rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.5)'
                      }
                    }}
                    onClick={() => setCurrentPage(p => Math.min(maxPage, p + 1))}
                    disabled={currentPage === maxPage}
                  >
                    İleri
                  </Button>
                </Box>
              </Paper>
            </Grid>
          )}
          {showSubmitButton && currentPage === maxPage && (
            <Grid item xs={12} className="form-preview-button-container">
              <Button 
                variant="contained" 
                className="form-preview-button"
                onClick={handleSubmit}
                disabled={submitting}
                sx={{
                  bgcolor: settings?.submitButtonColor || '#1976d2',
                  color: settings?.submitButtonTextColor || 'white',
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  borderRadius: 1,
                  fontFamily: settings?.fontFamily || 'inherit',
                  fontSize: settings?.fontSize === 'small' ? '15px' : settings?.fontSize === 'large' ? '19px' : '17px',
                  '&:hover': {
                    bgcolor: settings?.submitButtonColor || '#1565c0',
                    filter: 'brightness(0.9)'
                  },
                  '&:disabled': {
                    bgcolor: '#bdbdbd',
                    color: '#757575'
                  }
                }}
              >
                {submitting ? 'Gönderiliyor...' : (settings?.submitButtonText || 'Gönder')}
              </Button>
            </Grid>
          )}
        </Grid>
        
        {/* Doküman Numarası - Sağ Alt Köşe */}
        {(settings?.documentPrefix && settings?.documentCode && 
          settings.documentPrefix.trim() !== '' && settings.documentCode.trim() !== '' &&
          settings.documentPrefix !== 'DOK' && settings.documentCode !== '001') && (
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 8, sm: 12, md: 16 },
              right: { xs: 8, sm: 12, md: 16 },
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(8px)',
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 0.8 },
              borderRadius: { xs: 1, sm: 1.5 },
              border: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.08)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              fontSize: { xs: '10px', sm: '11px', md: '12px' },
              zIndex: 10
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: 'Roboto Mono, monospace',
                fontSize: { xs: '9px', sm: '10px', md: '11px' },
                fontWeight: 600,
                color: 'text.secondary',
                letterSpacing: '0.5px'
              }}
            >
              {`${settings.documentPrefix}-${settings.documentCode}`}
            </Typography>
            {settings.department && (
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: '8px', sm: '9px', md: '10px' },
                  color: 'text.disabled',
                  fontWeight: 500,
                  '&::before': {
                    content: '"•"',
                    mx: 0.5,
                    color: 'grey.400'
                  }
                }}
              >
                {settings.department}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

        {/* Onay Modalı */}
        <Dialog
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Form Gönderimi
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography>
                Formu göndermeden önce girdiğiniz bilgileri kontrol etmek ister misiniz?
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setConfirmModalOpen(false);
                  setPreviewModalOpen(true);
                }}
                startIcon={<EyeOutlined />}
              >
                Önizleme
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmModalOpen(false)}>
              İptal
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmSubmit}
              color="primary"
              disabled={submitting}
            >
              {submitting ? 'Gönderiliyor...' : 'Gönder'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Önizleme Modalı */}
        <Dialog
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 3,
              py: 2
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Form Önizleme
              </Typography>
              <IconButton
                onClick={() => setPreviewModalOpen(false)}
                size="small"
                sx={{
                  color: 'grey.500',
                  '&:hover': {
                    color: 'grey.700'
                  }
                }}
              >
                <CloseOutlined />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {renderFormPreview()}
          </DialogContent>
          <DialogActions
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              px: 3,
              py: 2
            }}
          >
            <Button
              onClick={() => setPreviewModalOpen(false)}
              variant="outlined"
              startIcon={<CloseOutlined />}
            >
              Düzenle
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setPreviewModalOpen(false);
                handleConfirmSubmit();
              }}
              startIcon={<CloudUploadOutlined />}
              disabled={submitting}
            >
              {submitting ? 'Gönderiliyor...' : 'Gönder'}
            </Button>
          </DialogActions>
        </Dialog>
    </Container>
    </>
  );
};

export default FormPreview;